import { useMemo } from 'react'
import { useRootContext } from 'app/services/RootProvider/RootProvider'
import useAccount from './useAccount'
import useAccountData from './useAccountData'
import useCart from './useCart'
import useCheckout from './useCheckout'
import useClaimables from './useClaimables'
import useCurrency from './useCurrency'

/**
 * The `useUser`
 *
 * @returns {object} the context
 */
function useUser () {
  const { appActivityStatus } = useRootContext()
  const {
    account,
    isUserActivityReady,
    loginUser,
    logoutUser,
    refreshAccountStatus,
    signUpAccessCode,
    signUpApply,
    signUpNewsletter,
    signUpPassportHolder,
    signUpStandardUser
  } = useAccount()
  const {
    accountData,
    hasAccountData,
    hasAccountDataError,
    isUpdatingAccountData,
    lastUpdatedAccountDataTimestamp,
    loadAccountData,
    loadAvatarEditTimestampsById
  } = useAccountData(account)
  const { preferredCurrency, setPreferredCurrency } = useCurrency()
  const {
    addToCart,
    cart,
    cartCount,
    cartTotalPrice,
    checkoutCart,
    checkoutSingle,
    clearCart,
    dismissCartLostSupply,
    isCartReady,
    lostSupplyCart,
    removeFromCart
  } = useCart(account)
  const {
    checkoutAccessoriesWithWallet,
    checkoutAvatarWithFreeMint,
    checkoutAvatarWithWallet,
    editAvatarWithFreeMint,
    editAvatarWithWallet,
    estimateAvatarGas
  } = useCheckout(account)
  const { claimWithShipping } = useClaimables(account)

  /**
   * Memo: The context object
   */
  const ctx = useMemo(
    () => ({
      account,
      accountData,
      addToCart,
      cart,
      cartCount,
      cartTotalPrice,
      checkoutAccessoriesWithWallet,
      checkoutAvatarWithFreeMint,
      checkoutAvatarWithWallet,
      checkoutCart,
      checkoutSingle,
      claimWithShipping,
      clearCart,
      dismissCartLostSupply,
      editAvatarWithFreeMint,
      editAvatarWithWallet,
      estimateAvatarGas,
      hasAccountData,
      hasAccountDataError,
      isCartReady,
      isUpdatingAccountData,
      isUserActivityReady,
      lastUpdatedAccountDataTimestamp,
      loadAccountData,
      loadAvatarEditTimestampsById,
      loginUser,
      logoutUser,
      lostSupplyCart,
      preferredCurrency,
      refreshAccountStatus,
      removeFromCart,
      setPreferredCurrency,
      signUpAccessCode,
      signUpApply,
      signUpNewsletter,
      signUpPassportHolder,
      signUpStandardUser,
      userActivityStatus: !isUserActivityReady ? 'BUSY' : appActivityStatus
    }),
    [
      account,
      accountData,
      addToCart,
      appActivityStatus,
      cart,
      cartCount,
      cartTotalPrice,
      checkoutAccessoriesWithWallet,
      checkoutAvatarWithFreeMint,
      checkoutAvatarWithWallet,
      checkoutCart,
      checkoutSingle,
      claimWithShipping,
      clearCart,
      dismissCartLostSupply,
      editAvatarWithFreeMint,
      editAvatarWithWallet,
      estimateAvatarGas,
      hasAccountData,
      hasAccountDataError,
      isCartReady,
      isUpdatingAccountData,
      isUserActivityReady,
      lastUpdatedAccountDataTimestamp,
      loadAccountData,
      loadAvatarEditTimestampsById,
      loginUser,
      logoutUser,
      lostSupplyCart,
      preferredCurrency,
      refreshAccountStatus,
      removeFromCart,
      setPreferredCurrency,
      signUpAccessCode,
      signUpApply,
      signUpNewsletter,
      signUpPassportHolder,
      signUpStandardUser
    ]
  )

  return ctx
}

// Export
export default useUser
